import React, { useState } from "react";
import { Plus, Trash2, KeyRound } from "lucide-react";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import type { LaunchableParameter, LaunchableParameterBinding } from "server";

const PARAMETER_NAME_REGEX = /^[A-Z_][A-Z0-9_]*$/;
const EMPTY_SELECT_VALUE = "__launchable_parameter_empty__";
const PARAMETER_TYPES = ["text", "choice"] as const;
type LaunchableParameterType = (typeof PARAMETER_TYPES)[number];

export const getLaunchableParameterNameError = (
  name: string,
  parameters: LaunchableParameter[],
  index: number,
): string | null => {
  const trimmedName = name.trim();
  if (!trimmedName) return "Name is required.";
  if (!PARAMETER_NAME_REGEX.test(trimmedName)) {
    return "Use uppercase letters, numbers, and underscores. Start with a letter or underscore.";
  }
  if (trimmedName.startsWith("BREV_")) {
    return "Names starting with BREV_ are reserved.";
  }
  const isDuplicate = parameters.some(
    (parameter, parameterIndex) =>
      parameterIndex !== index && parameter.name.trim() === trimmedName,
  );
  if (isDuplicate) return "Parameter names must be unique.";
  return null;
};

export const getLaunchableParameterType = (
  parameter: LaunchableParameter,
): LaunchableParameterType => (parameter.choice ? "choice" : "text");

export const getLaunchableParameterChoiceValues = (
  parameter: LaunchableParameter,
): string[] =>
  (parameter.choice?.choices || [])
    .map((choice) => choice.trim())
    .filter((choice) => choice !== "");

export const getLaunchableParameterDefaultValue = (
  parameter: LaunchableParameter,
): string => {
  if (getLaunchableParameterType(parameter) === "choice") {
    return parameter.choice?.defaultValue?.trim() || "";
  }
  return parameter.text?.defaultValue || "";
};

export const getLaunchableParameterChoiceError = (
  parameter: LaunchableParameter,
): string | null => {
  if (getLaunchableParameterType(parameter) !== "choice") return null;

  const rawChoices = parameter.choice?.choices || [];
  const choices = rawChoices.map((choice) => choice.trim());
  const nonEmptyChoices = choices.filter((choice) => choice !== "");

  if (nonEmptyChoices.length === 0) return "Add at least one choice.";
  if (choices.some((choice) => choice === "")) {
    return "Choice values cannot be blank.";
  }

  const uniqueChoices = new Set(nonEmptyChoices);
  if (uniqueChoices.size !== nonEmptyChoices.length) {
    return "Choice values must be unique.";
  }

  const defaultValue = parameter.choice?.defaultValue?.trim();
  if (defaultValue && !uniqueChoices.has(defaultValue)) {
    return "Default must match one of the choices.";
  }

  return null;
};

export const hasLaunchableParameterErrors = (
  parameters: LaunchableParameter[],
): boolean =>
  parameters.some(
    (parameter, index) =>
      Boolean(
        getLaunchableParameterNameError(parameter.name, parameters, index),
      ) || Boolean(getLaunchableParameterChoiceError(parameter)),
  );

export const normalizeLaunchableParameters = (
  parameters: LaunchableParameter[],
): LaunchableParameter[] =>
  parameters.map((parameter) => {
    const normalizedParameter: LaunchableParameter = {
      name: parameter.name.trim(),
      description: parameter.description?.trim() || undefined,
      required: parameter.required,
    };

    if (getLaunchableParameterType(parameter) === "choice") {
      const choices = getLaunchableParameterChoiceValues(parameter);
      const defaultValue = parameter.choice?.defaultValue?.trim();
      if (choices.length > 0) {
        normalizedParameter.choice = {
          choices,
          defaultValue: defaultValue || undefined,
        };
      }
      return normalizedParameter;
    }

    if (parameter.text?.defaultValue) {
      normalizedParameter.text = {
        defaultValue: parameter.text.defaultValue,
      };
    }

    return normalizedParameter;
  });

export const orderLaunchableParametersForPresentation = (
  parameters: LaunchableParameter[],
): LaunchableParameter[] => {
  const normalizedParameters = normalizeLaunchableParameters(parameters);

  return [
    ...normalizedParameters.filter((parameter) => parameter.required),
    ...normalizedParameters.filter((parameter) => !parameter.required),
  ];
};

interface LaunchableParameterDefinitionEditorProps {
  parameters: LaunchableParameter[];
  onChange: (parameters: LaunchableParameter[]) => void;
  disabled?: boolean;
  showErrors?: boolean;
}

export const LaunchableParameterDefinitionEditor: React.FC<
  LaunchableParameterDefinitionEditorProps
> = ({ parameters, onChange, disabled, showErrors = false }) => {
  const [touchedNames, setTouchedNames] = useState<Record<number, boolean>>({});

  const markNameTouched = (index: number) => {
    setTouchedNames((current) => ({ ...current, [index]: true }));
  };

  const addParameter = () => {
    onChange([
      ...parameters,
      { name: "", description: "", required: true, text: {} },
    ]);
  };

  const updateParameter = (
    index: number,
    updates: Partial<LaunchableParameter>,
  ) => {
    onChange(
      parameters.map((parameter, parameterIndex) =>
        parameterIndex === index ? { ...parameter, ...updates } : parameter,
      ),
    );
  };

  const removeParameter = (index: number) => {
    onChange(
      parameters.filter((_, parameterIndex) => parameterIndex !== index),
    );
  };

  const setParameterType = (
    index: number,
    parameterType: LaunchableParameterType,
  ) => {
    const parameter = parameters[index];
    if (!parameter) return;

    const existingDefaultValue = getLaunchableParameterDefaultValue(parameter);

    if (parameterType === "choice") {
      updateParameter(index, {
        text: undefined,
        choice: {
          choices:
            parameter.choice?.choices && parameter.choice.choices.length > 0
              ? parameter.choice.choices
              : existingDefaultValue
                ? [existingDefaultValue]
                : [""],
          defaultValue:
            parameter.choice?.defaultValue ||
            (existingDefaultValue ? existingDefaultValue : undefined),
        },
      });
      return;
    }

    updateParameter(index, {
      choice: undefined,
      text: existingDefaultValue ? { defaultValue: existingDefaultValue } : {},
    });
  };

  const updateChoiceValue = (
    parameterIndex: number,
    choiceIndex: number,
    value: string,
  ) => {
    const parameter = parameters[parameterIndex];
    if (!parameter) return;

    const choices = [...(parameter.choice?.choices || [])];
    const previousChoice = choices[choiceIndex];
    choices[choiceIndex] = value;
    const defaultValue = parameter.choice?.defaultValue;

    updateParameter(parameterIndex, {
      choice: {
        choices,
        defaultValue:
          defaultValue && defaultValue.trim() === previousChoice?.trim()
            ? value
            : defaultValue,
      },
    });
  };

  const addChoiceValue = (parameterIndex: number) => {
    const parameter = parameters[parameterIndex];
    if (!parameter) return;

    updateParameter(parameterIndex, {
      choice: {
        choices: [...(parameter.choice?.choices || []), ""],
        defaultValue: parameter.choice?.defaultValue,
      },
    });
  };

  const removeChoiceValue = (parameterIndex: number, choiceIndex: number) => {
    const parameter = parameters[parameterIndex];
    if (!parameter) return;

    const choices = (parameter.choice?.choices || []).filter(
      (_, index) => index !== choiceIndex,
    );
    const defaultValue = parameter.choice?.defaultValue;
    const normalizedChoices = choices.map((choice) => choice.trim());

    updateParameter(parameterIndex, {
      choice: {
        choices,
        defaultValue:
          defaultValue && normalizedChoices.includes(defaultValue.trim())
            ? defaultValue
            : undefined,
      },
    });
  };

  return (
    <div className="space-y-3 rounded-md border border-zinc-800 bg-secondaryBg p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-highlight" />
            <h3 className="text-sm font-semibold text-primary">
              Launch parameters
            </h3>
          </div>
          <p className="mt-1 text-xs text-secondary">
            Add values deployers provide at launch. They are available as
            environment variables during setup and container startup.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          className="gap-2 self-start"
          onClick={addParameter}
          disabled={disabled}
        >
          <Plus className="h-4 w-4" />
          Add Parameter
        </Button>
      </div>

      {parameters.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-700 px-3 py-4 text-sm text-secondary">
          No launch parameters configured.
        </div>
      ) : (
        <div className="space-y-3">
          {parameters.map((parameter, index) => {
            const nameError = getLaunchableParameterNameError(
              parameter.name,
              parameters,
              index,
            );
            const parameterType = getLaunchableParameterType(parameter);
            const isChoiceParameter = parameterType === "choice";
            const choiceValues = parameter.choice?.choices || [];
            const normalizedChoiceValues = Array.from(
              new Set(getLaunchableParameterChoiceValues(parameter)),
            );
            const choiceError = getLaunchableParameterChoiceError(parameter);
            const shouldShowNameError =
              Boolean(nameError) &&
              (showErrors || touchedNames[index] || !!parameter.name.trim());
            const shouldShowChoiceError =
              Boolean(choiceError) && (showErrors || isChoiceParameter);
            const choiceDefaultValue =
              parameter.choice?.defaultValue?.trim() || EMPTY_SELECT_VALUE;

            return (
              <div
                key={index}
                className="rounded-md border border-zinc-800 bg-primaryBg p-3"
              >
                <div className="grid gap-3 lg:grid-cols-[minmax(180px,240px)_1fr_auto_auto] lg:items-start">
                  <div>
                    <label
                      htmlFor={`launchable-parameter-name-${index}`}
                      className="text-xs font-medium text-secondary"
                    >
                      Name
                    </label>
                    <Input
                      id={`launchable-parameter-name-${index}`}
                      value={parameter.name}
                      onChange={(event) => {
                        markNameTouched(index);
                        updateParameter(index, {
                          name: event.target.value.toUpperCase(),
                        });
                      }}
                      onBlur={() => markNameTouched(index)}
                      placeholder="API_TOKEN"
                      disabled={disabled}
                      className="mt-1"
                      inputClassName="font-mono"
                    />
                    {shouldShowNameError && (
                      <p className="mt-1 text-xs text-red-500">{nameError}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor={`launchable-parameter-description-${index}`}
                      className="text-xs font-medium text-secondary"
                    >
                      Description
                    </label>
                    <Input
                      id={`launchable-parameter-description-${index}`}
                      value={parameter.description || ""}
                      onChange={(event) =>
                        updateParameter(index, {
                          description: event.target.value,
                        })
                      }
                      placeholder="Used by the setup script"
                      disabled={disabled}
                      className="mt-1"
                    />
                  </div>

                  <label className="flex items-center gap-2 pt-7 text-sm text-secondary">
                    <input
                      type="checkbox"
                      checked={parameter.required}
                      onChange={(event) =>
                        updateParameter(index, {
                          required: event.target.checked,
                        })
                      }
                      disabled={disabled}
                      className="h-4 w-4 rounded border-zinc-700 bg-primaryBg text-highlight focus:ring-highlight"
                    />
                    Required
                  </label>

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => removeParameter(index)}
                    disabled={disabled}
                    className="mt-6 h-9 w-9 justify-center px-0"
                    aria-label={`Remove parameter ${parameter.name || index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="mt-3">
                  <label className="text-xs font-medium text-secondary">
                    Type
                  </label>
                  <div className="mt-1 inline-flex h-9 rounded-md border border-zinc-800 bg-secondaryBg p-0.5">
                    {PARAMETER_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        aria-pressed={parameterType === type}
                        onClick={() => setParameterType(index, type)}
                        disabled={disabled}
                        className={`rounded px-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          parameterType === type
                            ? "bg-zinc-800 text-primary"
                            : "text-secondary hover:text-primary"
                        }`}
                      >
                        {type === "text" ? "Text" : "Choice"}
                      </button>
                    ))}
                  </div>
                </div>

                {isChoiceParameter ? (
                  <div className="mt-3 space-y-3">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-xs font-medium text-secondary">
                          Choices
                        </label>
                        <Button
                          type="button"
                          variant="secondary"
                          className="h-8 gap-1 px-2 text-xs"
                          onClick={() => addChoiceValue(index)}
                          disabled={disabled}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add
                        </Button>
                      </div>
                      <div className="mt-1 space-y-2">
                        {choiceValues.length === 0 ? (
                          <div className="rounded-md border border-dashed border-zinc-700 px-3 py-2 text-sm text-secondary">
                            No choices configured.
                          </div>
                        ) : (
                          choiceValues.map((choice, choiceIndex) => (
                            <div
                              key={choiceIndex}
                              className="flex items-center gap-2"
                            >
                              <Input
                                value={choice}
                                onChange={(event) =>
                                  updateChoiceValue(
                                    index,
                                    choiceIndex,
                                    event.target.value,
                                  )
                                }
                                placeholder="meta/llama3"
                                disabled={disabled}
                                hasError={
                                  shouldShowChoiceError && choice.trim() === ""
                                }
                                aria-label={`Choice ${choiceIndex + 1} for ${
                                  parameter.name || "parameter"
                                }`}
                              />
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() =>
                                  removeChoiceValue(index, choiceIndex)
                                }
                                disabled={disabled}
                                className="h-9 w-9 shrink-0 justify-center px-0"
                                aria-label={`Remove choice ${
                                  choice || choiceIndex + 1
                                }`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                      {shouldShowChoiceError && (
                        <p className="mt-1 text-xs text-red-500">
                          {choiceError}
                        </p>
                      )}
                    </div>

                    <div className="max-w-lg">
                      <label
                        htmlFor={`launchable-parameter-choice-default-${index}`}
                        className="text-xs font-medium text-secondary"
                      >
                        Default
                      </label>
                      <Select
                        value={choiceDefaultValue}
                        onValueChange={(value) =>
                          updateParameter(index, {
                            choice: {
                              choices: parameter.choice?.choices || [],
                              defaultValue:
                                value === EMPTY_SELECT_VALUE
                                  ? undefined
                                  : value,
                            },
                          })
                        }
                        disabled={
                          disabled || normalizedChoiceValues.length === 0
                        }
                      >
                        <SelectTrigger
                          id={`launchable-parameter-choice-default-${index}`}
                          className="mt-1"
                          hasError={shouldShowChoiceError}
                        >
                          <SelectValue placeholder="No default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={EMPTY_SELECT_VALUE}>
                            No default
                          </SelectItem>
                          {normalizedChoiceValues.map((choice) => (
                            <SelectItem key={choice} value={choice}>
                              {choice}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 max-w-lg">
                    <label
                      htmlFor={`launchable-parameter-text-default-${index}`}
                      className="text-xs font-medium text-secondary"
                    >
                      Default
                    </label>
                    <Input
                      id={`launchable-parameter-text-default-${index}`}
                      value={parameter.text?.defaultValue || ""}
                      onChange={(event) =>
                        updateParameter(index, {
                          choice: undefined,
                          text: { defaultValue: event.target.value },
                        })
                      }
                      placeholder="Optional default value"
                      disabled={disabled}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface LaunchableParameterBindingFormProps {
  parameters: LaunchableParameter[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  disabled?: boolean;
}

export const getLaunchableParameterEffectiveValue = (
  parameter: LaunchableParameter,
  values: Record<string, string>,
): string =>
  Object.prototype.hasOwnProperty.call(values, parameter.name)
    ? values[parameter.name] || ""
    : getLaunchableParameterDefaultValue(parameter);

export const getLaunchableParameterBindings = (
  parameters: LaunchableParameter[],
  values: Record<string, string>,
): LaunchableParameterBinding[] =>
  normalizeLaunchableParameters(parameters)
    .map((parameter) => ({
      name: parameter.name,
      value: getLaunchableParameterEffectiveValue(parameter, values),
    }))
    .filter((binding) => binding.value !== "");

export const hasMissingRequiredParameterBindings = (
  parameters: LaunchableParameter[],
  values: Record<string, string>,
): boolean =>
  normalizeLaunchableParameters(parameters).some(
    (parameter) =>
      parameter.required &&
      !getLaunchableParameterEffectiveValue(parameter, values),
  );

export const LaunchableParameterBindingForm: React.FC<
  LaunchableParameterBindingFormProps
> = ({ parameters, values, onChange, disabled }) => {
  const normalizedParameters =
    orderLaunchableParametersForPresentation(parameters);
  const [touchedParameters, setTouchedParameters] = useState<
    Record<string, boolean>
  >({});

  if (normalizedParameters.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 border-t border-zinc-800 px-4 py-3">
      <div>
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-highlight" />
          <p className="text-sm font-medium text-secondary">Setup values</p>
        </div>
        <p className="mt-1 text-xs text-secondary">
          These values are passed as environment variables while the Launchable
          starts.
        </p>
      </div>

      {normalizedParameters.map((parameter) => {
        const choiceValues = Array.from(
          new Set(getLaunchableParameterChoiceValues(parameter)),
        );
        const isChoiceParameter = choiceValues.length > 0;
        const value = getLaunchableParameterEffectiveValue(parameter, values);
        const isMissingRequired = parameter.required && !value;

        return (
          <div key={parameter.name} className="space-y-1">
            <label
              htmlFor={`launchable-parameter-binding-${parameter.name}`}
              className="flex items-center justify-between gap-2 text-xs font-medium text-secondary"
            >
              <span className="font-mono text-primary">
                {parameter.name}
                {parameter.required && (
                  <TooltipPrimitives.Provider>
                    <TooltipPrimitives.Root delayDuration={0}>
                      <TooltipPrimitives.Trigger asChild>
                        <span
                          className="ml-1 cursor-help text-red-500"
                          aria-label="Required to deploy"
                          tabIndex={0}
                        >
                          *
                        </span>
                      </TooltipPrimitives.Trigger>
                      <TooltipPrimitives.Portal>
                        <TooltipPrimitives.Content
                          side="top"
                          sideOffset={4}
                          className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-xs text-white shadow-md"
                        >
                          Required to deploy
                          <TooltipPrimitives.Arrow className="fill-zinc-800" />
                        </TooltipPrimitives.Content>
                      </TooltipPrimitives.Portal>
                    </TooltipPrimitives.Root>
                  </TooltipPrimitives.Provider>
                )}
              </span>
            </label>
            {parameter.description && (
              <p className="text-xs text-secondary">{parameter.description}</p>
            )}
            {isChoiceParameter ? (
              <Select
                value={value || EMPTY_SELECT_VALUE}
                onValueChange={(selectValue) => {
                  onChange({
                    ...values,
                    [parameter.name]:
                      selectValue === EMPTY_SELECT_VALUE ? "" : selectValue,
                  });
                  setTouchedParameters((current) => ({
                    ...current,
                    [parameter.name]: true,
                  }));
                }}
                disabled={disabled}
              >
                <SelectTrigger
                  id={`launchable-parameter-binding-${parameter.name}`}
                  hasError={
                    isMissingRequired && touchedParameters[parameter.name]
                  }
                >
                  <SelectValue placeholder={`Value for ${parameter.name}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_SELECT_VALUE}>
                    {parameter.required ? "Select a value" : "No value"}
                  </SelectItem>
                  {choiceValues.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={`launchable-parameter-binding-${parameter.name}`}
                type="password"
                value={value}
                onChange={(event) =>
                  onChange({ ...values, [parameter.name]: event.target.value })
                }
                onBlur={() =>
                  setTouchedParameters({
                    ...touchedParameters,
                    [parameter.name]: true,
                  })
                }
                placeholder={`Value for ${parameter.name}`}
                disabled={disabled}
                hasError={
                  isMissingRequired && touchedParameters[parameter.name]
                }
                inputClassName="font-mono"
              />
            )}
            {isMissingRequired && touchedParameters[parameter.name] && (
              <p className="text-xs text-red-500">
                This value is required to deploy.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
